import React, { useId } from 'react';
import { CustomIcon } from '../CustomIcon/CustomIcon';
import { isNavigableUrl } from '../../utils/safeUrl';
import './CookieConsentBanner.scss';

/**
 * CookieConsentBanner — cookie consent notice markup for Poly-delivered pages.
 *
 * Deliberately stateless and free of `useState` and `useEffect`. Visibility depends on the
 * `poly-consent` cookie, which only the client can read, so any server-rendered visibility
 * state would be a hydration mismatch. The markup ships hidden in the initial HTML and
 * `poly-consent-manager-v1.js` takes over: it binds the control ids below and toggles
 * `hidden` plus the `--visible` class. Shipping the markup rather than injecting it also
 * keeps the banner out of the CLS budget.
 *
 * This is a Global Element, not a hydrated block. The component emits no hydration attributes,
 * but PolyR wraps every entity in a `data-poly-component-type` div, so `hydrateComponents()`
 * skips this type by name and leaves the served markup untouched. Behaviour on this banner must
 * live in the consent manager: JSX handlers or state added here never attach and will silently
 * do nothing.
 *
 * A labelled `region`, not a modal dialog. `aria-modal` would remove the whole page from
 * assistive technology the moment the runtime reveals the banner, while mouse users keep
 * browsing freely — nothing here inerts the page, locks scrolling or obscures anything
 * behind a backdrop. ARIA marks a dialog modal only when code prevents interaction outside
 * it and styling obscures it; this is a bottom bar with a `border-top`, so the honest
 * framing is a region that is announced and reachable alongside the rest of the page.
 *
 * The region role is implicit in the `<section>` element and so is not declared: a `<section>`
 * carries it once the element has an accessible name, and is exposed as a generic container
 * with no name. Naming it is therefore what makes it a landmark, and the name comes from the
 * visible `<h2>` via `aria-labelledby` so it cannot drift from the rendered title. That id, the
 * body id and the category description ids are generated per instance: `#poly-consent-banner`
 * and the control ids are fixed because the consent runtime binds to them, but nothing binds to
 * these, and Storybook's Docs view renders every story on one page — duplicated ids would
 * resolve every banner's name and every category's description to the first banner's.
 *
 * A bare `useId` value is unique only within one render tree. PolyRenderingService renders each
 * entity in its own render call, so `useId` restarts for every entity and two components on one
 * page both emit the same first id. The generated ids therefore carry a `poly-consent-` prefix,
 * which keeps them unique against every other component on a real page as well as between
 * instances in one tree.
 *
 * The bar shows the notice and three decisions: continue without accepting (which records the
 * reject-optional posture), manage preferences, and accept all. A site can turn the first one
 * off with `showRejectOptional`, since some marketplaces' banners offer only the two buttons.
 * The per-category checkboxes and "Save preferences" sit in a panel that stays hidden until
 * the seller asks to manage preferences; the consent runtime opens it through the manage
 * control's `aria-controls`.
 *
 * The `<h2>` is visually hidden: the design shows no heading, but the heading is what names the
 * region and where the runtime moves focus. It carries `tabindex="-1"` so the consent runtime
 * has a static element to focus when the banner opens. `__content` scrolls, and the
 * actions row sits below it, so focusing the first control instead can leave the heading and
 * body scrolled out of view — and lands the seller on the most permissive of the three
 * choices, on a surface where declining must be no harder than accepting. The runtime
 * resolves the target through `aria-labelledby` rather than a shared id literal, which is
 * why that attribute is load-bearing beyond naming.
 *
 * Documented exception to this package's `cloudscape-core-usage` rule: the controls are native
 * `<button>`, `<input type="checkbox">` and `<a>` rather than Cloudscape `Button`, `Checkbox`
 * and `Link`.
 *
 * The ids listed below are a binding contract with `poly-consent-manager-v1.js`, which resolves
 * each control through `document.getElementById` and narrows it with
 * `instanceof HTMLInputElement` before reading a category. Cloudscape's `id` prop is documented
 * as applying to a component's *root* element and is deprecated as reserved for internal use,
 * so it does not guarantee the id reaches the element the runtime needs. On `Checkbox` the root
 * is a wrapper `<span>`: an id passed that way fails the narrowing and drops that category from
 * the recorded decision with no error. `controlId` does reach the native input, but which
 * element carries the id stays Cloudscape's choice to change, and a consent category that
 * silently stops being recorded is not a failure this contract can absorb.
 *
 * `Checkbox` is also controlled — `checked` is required and there is no uncontrolled mode — so
 * adopting it would reintroduce the state this component is deliberately without, while the
 * runtime assigns `checkbox.checked` imperatively rather than through React.
 *
 * Ids the runtime owns: `#poly-consent-banner`, `#poly-consent-accept-all`,
 * `#poly-consent-reject-optional`, `#poly-consent-save-preferences`,
 * `#poly-consent-manage-preferences`, and
 * `#poly-consent-category-operational`,
 * `-performance` and `-advertising`.
 */

/** One consent category row. */
interface ConsentCategoryRow {
  id: string;
  label: string;
  description: string;
  /** Rendered checked and disabled — this category is never a choice. */
  alwaysOn?: boolean;
  defaultChecked?: boolean;
}

export interface CookieConsentBannerProps {
  /** Region name read by screen readers. Visually hidden. */
  heading?: string;
  /** Notice copy shown in the bar. */
  body?: string;
  /** Label for the accept-everything control. */
  acceptAllText?: string;
  /** Label for the link-style control that declines optional cookies and closes the bar. */
  rejectOptionalText?: string;
  /** Shows the control that declines optional cookies. Turn off for sites whose banner offers only the two buttons. */
  showRejectOptional?: boolean;
  /** Label for the control that opens the per-category preferences. */
  managePreferencesText?: string;
  /** Label for the control that records the per-category selection. */
  savePreferencesText?: string;
  /**
   * Cookie policy URL. The policy sentence is omitted when absent or not a navigable URL.
   * @polyType Url
   */
  policyUrl?: string;
  /** Sentence placed before the policy link, ending where the link text begins. */
  policyLeadText?: string;
  /** Cookie policy link text. */
  policyLinkText?: string;
  /** Heading above the category rows. */
  categoriesHeading?: string;
  /** Label for the operational category. */
  operationalLabel?: string;
  /** What the operational category covers. */
  operationalDescription?: string;
  /** Label for the performance category. */
  performanceLabel?: string;
  /** What the performance category covers. */
  performanceDescription?: string;
  /** Label for the advertising category. */
  advertisingLabel?: string;
  /** What the advertising category covers. */
  advertisingDescription?: string;
  /**
   * Initial checked state of the performance control.
   *
   * The consent manager overwrites this from the recorded decision when it opens the
   * banner, so it only governs the pre-script paint.
   */
  performanceDefaultChecked?: boolean;
  /** Initial checked state of the advertising control. See {@link performanceDefaultChecked}. */
  advertisingDefaultChecked?: boolean;
}

export const CookieConsentBanner: React.FC<CookieConsentBannerProps> = ({
  heading = 'Cookie preferences',
  body = 'On this site, we use cookies and similar tools (collectively, "cookies") to provide and improve services to you and to show ads.',
  acceptAllText = 'Accept all cookies',
  rejectOptionalText = 'Continue without accepting',
  showRejectOptional = true,
  managePreferencesText = 'Manage preferences',
  savePreferencesText = 'Save preferences',
  policyUrl,
  policyLeadText = 'To learn more about how Amazon uses cookies, please read the',
  policyLinkText = 'Amazon Cookies Notice',
  categoriesHeading = 'Cookie categories',
  operationalLabel = 'Operational',
  operationalDescription = 'Required for the site to function. These cannot be turned off.',
  performanceLabel = 'Performance',
  performanceDescription = 'Helps us understand how the site is used so we can improve it.',
  advertisingLabel = 'Advertising',
  advertisingDescription = 'Used to show you relevant offers on and off Amazon.',
  performanceDefaultChecked = false,
  advertisingDefaultChecked = false,
}) => {
  const instanceId = `poly-consent-${useId()}`;
  const headingId = `${instanceId}-heading`;
  const bodyId = `${instanceId}-body`;
  const preferencesId = `${instanceId}-preferences`;

  const categories: ConsentCategoryRow[] = [
    {
      id: 'poly-consent-category-operational',
      label: operationalLabel,
      description: operationalDescription,
      alwaysOn: true,
    },
    {
      id: 'poly-consent-category-performance',
      label: performanceLabel,
      description: performanceDescription,
      defaultChecked: performanceDefaultChecked,
    },
    {
      id: 'poly-consent-category-advertising',
      label: advertisingLabel,
      description: advertisingDescription,
      defaultChecked: advertisingDefaultChecked,
    },
  ];

  return (
    <section
      id="poly-consent-banner"
      className="poly-consent-banner"
      aria-labelledby={headingId}
      aria-describedby={bodyId}
      hidden
    >
      <div className="poly-consent-banner__content">
        <h2 id={headingId} className="poly-consent-banner__heading poly-visually-hidden" tabIndex={-1}>
          {heading}
        </h2>
        <p id={bodyId} className="poly-consent-banner__body">
          {body}
          {isNavigableUrl(policyUrl) && (
            <>
              {' '}{policyLeadText}{' '}
              <a href={policyUrl} className="poly-consent-banner__policy-link">{policyLinkText}</a>.
            </>
          )}
        </p>

        <div id={preferencesId} className="poly-consent-banner__preferences" hidden>
          <fieldset className="poly-consent-banner__categories">
            <legend className="poly-consent-banner__categories-legend">{categoriesHeading}</legend>
            {categories.map(({ id, label, description, alwaysOn, defaultChecked }) => {
              const descriptionId = `${instanceId}-${id}-description`;
              return (
                <div key={id} className="poly-consent-banner__category">
                  {/*
                    Wrapping rather than `htmlFor`: the control id is shared by every instance, and
                    a `for` reference resolves to the first match, so a later banner's label would
                    name and toggle the first banner's checkbox.
                  */}
                  <label className="poly-consent-banner__category-label">
                    <input
                      type="checkbox"
                      id={id}
                      className="poly-consent-banner__category-control"
                      defaultChecked={alwaysOn || defaultChecked}
                      disabled={alwaysOn}
                      aria-describedby={descriptionId}
                    />
                    {label}
                  </label>
                  <p id={descriptionId} className="poly-consent-banner__category-description">
                    {description}
                  </p>
                </div>
              );
            })}
          </fieldset>
          <button
            id="poly-consent-save-preferences"
            type="button"
            className="poly-consent-banner__btn poly-consent-banner__btn--light"
          >
            {savePreferencesText}
          </button>
        </div>
      </div>

      <div className="poly-consent-banner__actions">
        {showRejectOptional && (
          <button
            id="poly-consent-reject-optional"
            type="button"
            className="poly-consent-banner__continue"
          >
            {rejectOptionalText}
            <span className="poly-consent-banner__continue-arrow" aria-hidden="true">
              <CustomIcon name="arrow-forward" size="1em" aria-hidden="true" />
            </span>
          </button>
        )}
        <div className="poly-consent-banner__buttons">
          <button
            id="poly-consent-manage-preferences"
            type="button"
            className="poly-consent-banner__btn poly-consent-banner__btn--light"
            aria-expanded="false"
            aria-controls={preferencesId}
          >
            {managePreferencesText}
          </button>
          <button
            id="poly-consent-accept-all"
            type="button"
            className="poly-consent-banner__btn poly-consent-banner__btn--primary"
          >
            {acceptAllText}
          </button>
        </div>
      </div>
    </section>
  );
};
